import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  PaperPlaneTilt,
  Sparkle,
  Star,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useLumi, LumiSentencePlayback, LumiInline } from "./LumiCharacter";
import { LumiReplayButton } from "./LumiReplayButton";
import { useKidsSound } from "@/hooks/useKidsSound";
import { loadActivityProgress, saveActivityProgress } from "@/lib/activityProgress";
import { speakLumi, playLumiBase64, stopLumiAudio, clearLumiReplay } from "@/lib/lumiAudio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shown instead of a percentage score so the result never contradicts Lumi's words.
const CHECKLIST = [
  ["words_used", "Used all the words"],
  ["complete_sentence", "Complete sentence (who or what + action)"],
  ["word_order", "Words in the right order"],
  ["capital_letter", "Starts with a capital letter"],
  ["end_punctuation", "Ends with . ! or ?"],
];

// The three levels of the approved activity bank (SentenceBuilder_ActivityBank v3.2 FINAL).
const LEVELS = [
  {
    id: 1,
    label: "Level 1 · Put the Sentence in Order",
    emoji: "🌸",
    desc: "Tap the word tiles to put them in an order that sounds right.",
    color: "from-green-200 to-emerald-300",
  },
  {
    id: 2,
    label: "Level 2 · Complete the Sentence",
    emoji: "🔮",
    desc: "Pick the word that makes the sentence fit.",
    color: "from-purple-200 to-pink-300",
  },
  {
    id: 3,
    label: "Level 3 · Build With Core Words",
    emoji: "⭐",
    desc: "Use all the magical words to write your own sentence.",
    color: "from-yellow-200 to-orange-300",
  },
];

const SentenceBuilder = ({ player, onBack, onUpdateScore }) => {
  const [level, setLevel] = useState(null); // 1 | 2 | 3 | null (picker)
  const [activity, setActivity] = useState(null);
  const [placed, setPlaced] = useState([]); // Level 1: indexes of scrambled_tiles, in the order the child placed them
  const [choice, setChoice] = useState(null); // Level 2
  const [sentence, setSentence] = useState(""); // Level 3 + Keep Building
  const [stage, setStage] = useState("base"); // "base" | "expansion" (Keep Building)
  const [baseSentence, setBaseSentence] = useState("");
  const [expansion, setExpansion] = useState(null);
  const [helpModel, setHelpModel] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingWords, setLoadingWords] = useState(false);
  // Round = which activity is on screen; it only advances when a new activity starts.
  const [round, setRound] = useState(1);
  const lumi = useLumi();
  const greetedRef = useRef(false);
  // Bumped on every answer and whenever the child goes back to editing.
  const attemptRef = useRef(0);
  const seenRef = useRef([]); // activity ids used in this pass through the level
  const activityIdRef = useRef(null);
  const completedRef = useRef([]); // activities finished (fully correct) in this pass
  const roundRef = useRef(1);
  const playerId = player?.id || null;
  // Progress per child + level: { seen, completed, current, round } — current = unfinished activity to resume.
  const saveProgress = (lvl, current) => saveActivityProgress("kids_sb_progress", playerId, lvl, {
    seen: seenRef.current, completed: completedRef.current, current, round: roundRef.current,
  });
  // Points are awarded once per activity; Keep Building re-checks never add them again.
  const awardedRef = useRef(null);
  // Sound follows the shared Kids Corner toggle (header). Lumi's audio never overlaps: see lib/lumiAudio.
  const [soundOn] = useKidsSound();

  // While Keep Building, Lumi's bubble is the one place the follow-up question lives, so it stays put.
  const backToSitting = () => {
    attemptRef.current += 1;
    if (stage === "expansion" && expansion?.question) lumi.say(expansion.question, "speaking", 0);
    else lumi.idle();
  };

  useEffect(() => {
    if (greetedRef.current) return;
    greetedRef.current = true;
    lumi.wave("Hi! I'm Lumi! Let's build some magical sentences! ✨", 3500);
    return () => { lumi.idle(); lumi.setInline(false); stopLumiAudio(); clearLumiReplay(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    lumi.setInline(!!level);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  const resetAnswer = () => {
    setResult(null);
    setPlaced([]);
    setChoice(null);
    setSentence("");
    setStage("base");
    setBaseSentence("");
    setExpansion(null);
    setHelpModel("");
  };

  const newChallenge = useCallback(async (lvl, fresh = false) => {
    setLoadingWords(true);
    resetAnswer();
    let resumeId = null;
    if (fresh) {
      // Entering a level: pick up the saved pass (unfinished activity, finished ones, round) for this child.
      const saved = loadActivityProgress("kids_sb_progress", playerId, lvl);
      seenRef.current = saved?.seen || [];
      completedRef.current = saved?.completed || [];
      activityIdRef.current = null;
      resumeId = saved?.current || null;
      // No unfinished activity but some finished → the next one is a new round.
      roundRef.current = (saved?.round || 1) + (!resumeId && completedRef.current.length ? 1 : 0);
      setRound(roundRef.current);
    }
    try {
      const res = await axios.post(`${API}/kids/sentence-challenge`, {
        level: lvl,
        previous_id: activityIdRef.current,
        seen_ids: seenRef.current,
        activity_id: resumeId,
      });
      const act = res.data;
      if (act.cycle_reset) { seenRef.current = []; completedRef.current = []; }
      if (!seenRef.current.includes(act.activity_id)) seenRef.current = [...seenRef.current, act.activity_id];
      activityIdRef.current = act.activity_id;
      setActivity(act);
      saveProgress(lvl, act.activity_id);
      if (act.resumed) toast("Welcome back! Let's finish this sentence.", { icon: "✨" });
    } catch (e) {
      toast.error("Lumi can't find words right now!");
    }
    setLoadingWords(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  useEffect(() => {
    if (level) { setActivity(null); newChallenge(level, true); }
  }, [level, newChallenge]);

  const leaveActivity = () => { attemptRef.current += 1; lumi.idle(); };
  const nextActivity = () => { leaveActivity(); stopLumiAudio(); clearLumiReplay(); roundRef.current += 1; setRound(roundRef.current); newChallenge(level); };

  const placedWords = activity ? placed.map((i) => activity.scrambled_tiles[i]) : [];
  const expanding = stage === "expansion";
  const hasAnswer = expanding
    ? sentence.trim().length >= 3
    : level === 1
      ? activity && placed.length === activity.scrambled_tiles.length
      : level === 2
        ? !!choice
        : sentence.trim().length >= 3;

  const submit = async (help = false) => {
    if (!activity || (!help && !hasAnswer)) return;
    const attempt = ++attemptRef.current;
    setIsChecking(true);
    stopLumiAudio();
    lumi.say("Let me look at your sentence…", "thinking", 0);
    try {
      const res = await axios.post(`${API}/kids/sentence-check`, {
        player_id: player?.id || null,
        activity_id: activity.activity_id,
        stage,
        tiles: expanding ? [] : placedWords,
        choice: expanding ? null : choice,
        sentence: sentence.trim(),
        base_sentence: baseSentence,
        help,
      }, { timeout: 20000 });
      if (attempt !== attemptRef.current) { setIsChecking(false); return; }
      const data = res.data;
      if (data.status === "help") {
        setHelpModel(data.expanded_model || "");
        backToSitting();
        setIsChecking(false);
        return;
      }
      setResult(data);
      setHelpModel("");
      if (data.status === "fully_correct") {
        if (!expanding) {
          if (!completedRef.current.includes(activity.activity_id)) completedRef.current = [...completedRef.current, activity.activity_id];
          saveProgress(level, null); // finished: nothing to resume, and it won't repeat until the set is done
          setBaseSentence(data.sentence || "");
          setExpansion(data.expansion || null);
          if (player && onUpdateScore && awardedRef.current !== activity.activity_id) {
            awardedRef.current = activity.activity_id;
            const badge = !player.badges?.includes("sentence_smith") ? "sentence_smith" : null;
            onUpdateScore(data.points || 30, badge);
          }
        }
        lumi.celebrateSentence(data.sentence || sentence.trim(), { holdPose: true });
      } else {
        lumi.setState("tryagain");
      }
      playLumiBase64(data.message, data.audio_base64, data.audio_format);
    } catch (err) {
      toast.error("Lumi is taking a break — try again!");
      lumi.idle();
    }
    setIsChecking(false);
  };

  // Keep Building: reopen the sentence that passed so the child can add the detail Lumi asked about.
  // The follow-up question stays visible and Lumi reads it right away (unless sound is off), so a child never
  // has to press Help Me just to hear the basic instruction. Activity, round and the child's sentence are kept.
  const keepBuilding = () => {
    backToSitting();
    setStage("expansion");
    setSentence(baseSentence);
    setResult(null);
    setHelpModel("");
    const question = expansion?.question;
    if (question) {
      lumi.say(question, "speaking", 0); // shown once, in Lumi's bubble; the directions box only names the step
      speakLumi(question);
    }
  };

  // Try again after a correction: the child's own answer stays so they can fix just that part.
  const tryAgain = () => { backToSitting(); setResult(null); };

  const [speakingWord, setSpeakingWord] = useState(null);
  const speakWord = async (w) => {
    if (!w || !soundOn) return;
    setSpeakingWord(w);
    await speakLumi(w);
    setSpeakingWord(null);
  };

  const appendWord = (w) => {
    const piece = String(w || "").trim();
    if (!piece) return;
    if (result) { backToSitting(); setResult(null); }
    setSentence((prev) => {
      const base = (prev || "").replace(/\s+$/, "");
      return base ? `${base} ${piece}` : piece;
    });
  };

  const placeTile = (i) => {
    if (placed.includes(i) || isChecking) return;
    if (result) { backToSitting(); setResult(null); }
    setPlaced((p) => [...p, i]);
  };
  const removeTile = (i) => {
    if (isChecking) return;
    if (result) { backToSitting(); setResult(null); }
    setPlaced((p) => p.filter((x) => x !== i));
  };

  // ---------- LEVEL PICKER ----------
  if (!level) {
    return (
      <div className="lumi-fade-in">
        <Button
          variant="outline"
          onClick={onBack}
          className="mb-6 font-nunito font-bold border border-[#d9ccf5] rounded-full bg-white text-[#4a2e8a] hover:bg-[#f3eefc]"
          data-testid="sentence-back-btn"
        >
          <ArrowLeft size={20} weight="bold" className="mr-2" />
          Back to Kid's Corner
        </Button>

        <div className="text-center mb-6">
          <h1 className="font-nunito font-extrabold text-4xl text-[#4a2e8a] mb-2">
            🧚‍♀️ Sentence Builder
          </h1>
          <p className="font-nunito text-lg text-muted-foreground">
            with Lumi the Word Fairy!
          </p>
        </div>

        <h2 className="font-nunito font-extrabold text-center text-xl text-[#4a2e8a] mb-4">
          Pick your level!
        </h2>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {LEVELS.map((item) => (
            <button
              key={item.id}
              className={`relative bg-gradient-to-br ${item.color} rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)] p-8 cursor-pointer lumi-tile-hover text-left`}
              onClick={() => setLevel(item.id)}
              data-testid={`sentence-level-${item.id}`}
              data-progress={loadActivityProgress("kids_sb_progress", playerId, item.id)?.completed?.length || undefined}
            >
              {(() => { const p = loadActivityProgress("kids_sb_progress", playerId, item.id); return p && (p.current || p.completed?.length) ? (
                <span className="absolute top-4 right-4 bg-[#6a3fc7] text-white font-nunito font-bold text-xs rounded-full px-3 py-1" data-testid={`sentence-level-${item.id}-progress`}>
                  {p.current ? `Continue · Round ${p.round || 1}` : `${p.completed.length} of 10 done`}
                </span>
              ) : null; })()}
              <div className="text-6xl text-center mb-4">{item.emoji}</div>
              <h2 className="font-nunito font-extrabold text-2xl text-[#4a2e8a] text-center mb-2">
                {item.label}
              </h2>
              <p className="font-nunito text-center text-foreground/80">{item.desc}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- GAME ----------
  const isCelebrating = result?.status === "fully_correct";
  const needsFix = result && result.status !== "fully_correct";
  const words = activity?.assigned_words || [];
  const builtSentence = placedWords.length
    ? placedWords.join(" ").replace(/^./, (c) => c.toUpperCase()) + (placed.length === activity.scrambled_tiles.length ? "." : "")
    : "";
  const inputLocked = isChecking || isCelebrating;

  return (
    <div className="max-w-3xl mx-auto lumi-fade-in">
      <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
        <Button
          variant="outline"
          onClick={() => { setLevel(null); leaveActivity(); stopLumiAudio(); clearLumiReplay(); }}
          className="font-nunito font-bold border border-[#d9ccf5] rounded-full bg-white text-[#4a2e8a] hover:bg-[#f3eefc]"
          data-testid="sentence-change-level-btn"
        >
          <ArrowLeft size={20} weight="bold" className="mr-2" />
          Change Level
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          {activity?.theme && (
            <div className="flex items-center gap-2 bg-[#ffe9f1] px-3 py-1 rounded-full border border-[#f6c6d8]">
              <Sparkle size={14} weight="fill" className="text-accent-pink" />
              <span className="font-nunito font-bold text-sm" data-testid="sentence-theme-label">Theme: {activity.theme}</span>
            </div>
          )}
          <div className="flex items-center gap-2 bg-[#efe9fb] px-3 py-1 rounded-full border border-[#d9ccf5]">
            <Sparkle size={16} weight="fill" className="text-accent-purple" />
            <span className="font-nunito font-bold text-sm" data-testid="sentence-round">Round {round}</span>
          </div>
          <LumiReplayButton testid="sentence-replay-btn" />
        </div>
      </div>

      {/* Directions straight from the activity bank */}
      <div className="bg-white rounded-2xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)] p-4 mb-6 text-center">
        <p className="font-nunito font-bold text-sm text-purple-700 mb-1" data-testid="sentence-level-name">
          {LEVELS.find((l) => l.id === level)?.label}
        </p>
        <p className="font-nunito text-foreground" data-testid="sentence-directions">
          {loadingWords || !activity
            ? "Finding magical words…"
            : expanding
              ? "Keep Building: add the detail Lumi asked about to your sentence."
              : activity.directions}
        </p>
      </div>
      <LumiInline className="mb-5" />

      {activity && !expanding && level === 1 && (
        <div className="bg-gradient-to-r from-[#fff5f9] to-[#f7f4fd] rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)] p-6 mb-6" data-testid="sentence-l1">
          <div className="flex flex-wrap gap-3 justify-center mb-4" data-testid="sentence-tiles">
            {activity.scrambled_tiles.map((w, i) => (
              <button
                type="button"
                key={w + i}
                disabled={placed.includes(i) || inputLocked}
                onClick={() => placeTile(i)}
                data-testid={`sentence-tile-${i}`}
                className={`px-4 py-2 font-nunito font-bold text-lg border-2 border-[#d9ccf5] rounded-full shadow-sm ${placed.includes(i) ? "bg-gray-100 opacity-40" : "bg-yellow-200 cursor-pointer hover:-translate-y-0.5"}`}
              >
                {w}
              </button>
            ))}
          </div>
          <div className="min-h-[64px] bg-white border-2 border-dashed border-[#c9b8f0] rounded-2xl p-3 flex flex-wrap gap-2 items-center justify-center" data-testid="sentence-placed">
            {placed.length === 0 && <span className="font-nunito text-muted-foreground text-sm">Tap the words in order…</span>}
            {placed.map((i, pos) => (
              <button
                type="button"
                key={`p${i}`}
                disabled={inputLocked}
                onClick={() => removeTile(i)}
                data-testid={`sentence-placed-${pos}`}
                className="px-4 py-2 font-nunito font-bold text-lg bg-[#d5f2ec] border-2 border-[#7fd1c3] rounded-full"
              >
                {activity.scrambled_tiles[i]}
              </button>
            ))}
          </div>
          {builtSentence && <p className="font-nunito text-center text-foreground mt-3" data-testid="sentence-built">{builtSentence}</p>}
        </div>
      )}

      {activity && !expanding && level === 2 && (
        <div className="bg-gradient-to-r from-[#fff5f9] to-[#f7f4fd] rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)] p-6 mb-6" data-testid="sentence-l2">
          <p className="font-nunito font-bold text-2xl text-center text-foreground mb-5" data-testid="sentence-frame">
            {activity.sentence_frame.replace("___", choice ? `“${choice}”` : "_____")}
          </p>
          <div className="flex flex-wrap gap-3 justify-center" data-testid="sentence-choices">
            {activity.answer_choices.map((c, i) => (
              <button
                type="button"
                key={c + i}
                disabled={inputLocked}
                onClick={() => { if (result) { backToSitting(); setResult(null); } setChoice(c); }}
                data-testid={`sentence-choice-${i}`}
                className={`px-5 py-2 font-nunito font-bold text-lg border-2 border-[#d9ccf5] rounded-full shadow-sm cursor-pointer ${choice === c ? "bg-green-300" : "bg-yellow-200 hover:-translate-y-0.5"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {activity && (level === 3 || expanding) && (
        <div className="bg-gradient-to-r from-[#fff5f9] to-[#f7f4fd] rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)] p-6 mb-6" data-testid="sentence-l3">
          {!expanding && (
            <>
              <div className="flex flex-wrap gap-3 justify-center" data-testid="sentence-word-chips">
                {words.map((w, i) => (
                  <button
                    type="button"
                    key={w + i}
                    onClick={() => { appendWord(w); speakWord(w); }}
                    aria-label={`Add and hear the word ${w}`}
                    data-testid={`sentence-word-chip-${i}`}
                    className={`lumi-chip-pop px-4 py-2 font-nunito font-bold text-lg border-2 border-[#d9ccf5] rounded-full shadow-sm cursor-pointer bg-yellow-200 hover:-translate-y-0.5 ${isCelebrating ? "lumi-chip-celebrate" : ""} ${speakingWord === w ? "ring-4 ring-accent-purple/50 scale-105" : ""}`}
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    {w}
                    {soundOn && <span className="ml-1.5 text-sm align-middle" aria-hidden="true">{speakingWord === w ? "🔊" : "🔈"}</span>}
                  </button>
                ))}
              </div>
              <p className="font-nunito text-xs text-center text-muted-foreground mt-3">
                Tap a word to add it to your sentence{soundOn ? " and hear Lumi say it 🔈" : ""}
              </p>
              {activity.sentence_starter && (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => { if (!sentence.trim()) setSentence(activity.sentence_starter + " "); }}
                    className="font-nunito font-bold text-sm bg-white border border-[#d9ccf5] rounded-full px-4 py-2 text-[#4a2e8a] hover:bg-[#f3eefc]"
                    data-testid="sentence-starter-btn"
                  >
                    ✨ Start with: “{activity.sentence_starter}…”
                  </button>
                </div>
              )}
            </>
          )}
          <Textarea
            value={sentence}
            onChange={(e) => { setSentence(e.target.value); if (result) { backToSitting(); setResult(null); } }}
            placeholder="Type your magical sentence here…"
            maxLength={300}
            disabled={inputLocked}
            className="font-nunito text-lg border-2 border-[#d9ccf5] rounded-2xl min-h-[100px] focus-visible:ring-[#b79cf0] resize-none mt-4"
            data-testid="sentence-input"
          />
          {expanding && (
            <p className="font-nunito text-sm text-[#4a2e8a] mt-3 text-center flex items-center justify-center gap-2 flex-wrap" data-testid="sentence-expansion-hint">
              Lumi's question is in her bubble. <LumiReplayButton testid="sentence-expansion-replay-btn" />
            </p>
          )}
          {expanding && expansion?.starter && !sentence.trim() && (
            <p className="font-nunito text-xs text-muted-foreground mt-2">Try starting with: {expansion.starter}</p>
          )}
          {expanding && helpModel && (
            <p className="font-nunito text-sm text-foreground mt-3 text-center" data-testid="sentence-help-model">Lumi says: {helpModel}</p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 flex-wrap mb-6">
        {!isCelebrating ? (
          <>
            {expanding && (
              <Button
                onClick={() => submit(true)}
                disabled={isChecking}
                variant="outline"
                className="font-nunito font-bold border border-[#d9ccf5] rounded-full px-6 py-5 bg-white text-[#4a2e8a] hover:bg-[#f3eefc]"
                data-testid="sentence-help-btn"
              >
                Help me, Lumi
              </Button>
            )}
            <Button
              onClick={() => submit(false)}
              disabled={isChecking || loadingWords || !hasAnswer}
              className="bg-[#6a3fc7] hover:bg-[#5a33ad] text-white font-nunito font-extrabold border-0 rounded-full px-6 py-5 focus-visible:ring-4 focus-visible:ring-[#b79cf0]"
              data-testid="sentence-submit-btn"
            >
              {isChecking ? (
                "Lumi is thinking…"
              ) : (
                <>
                  <PaperPlaneTilt size={20} weight="fill" className="mr-2" />
                  {needsFix ? "Show Lumi Again!" : "Show Lumi!"}
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            {!expanding && expansion && (
              <Button
                onClick={keepBuilding}
                className="bg-[#2fa6e0] hover:bg-[#2590c6] text-white font-nunito font-extrabold border-0 rounded-full px-6 py-5"
                data-testid="sentence-keep-building-btn"
              >
                Keep Building ✏️
              </Button>
            )}
            <Button
              onClick={nextActivity}
              className="bg-[#ff8518] hover:bg-[#e8740f] text-white font-nunito font-extrabold border-0 rounded-full px-6 py-5"
              data-testid="sentence-next-btn"
            >
              <ArrowsClockwise size={20} weight="bold" className="mr-2" />
              New Words!
            </Button>
          </>
        )}
      </div>

      {/* Feedback: one Lumi message, shown once */}
      {result && (
        <div
          className={`lumi-fade-in rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)] p-6 ${
            needsFix ? "bg-gradient-to-br from-blue-50 to-purple-50" : "bg-gradient-to-br from-yellow-50 to-pink-50"
          }`}
          data-testid="sentence-result"
        >
          {isCelebrating && !expanding && (
            <div className="flex items-center gap-2 justify-center mb-4">
              {[1, 2, 3].map((n) => (
                <Star
                  key={n}
                  size={36}
                  weight={n <= result.stars ? "fill" : "duotone"}
                  className={n <= result.stars ? "text-yellow-500" : "text-muted-foreground/30"}
                />
              ))}
            </div>
          )}
          {isCelebrating && <LumiSentencePlayback />}

          {result.checklist && (
            <ul className="mx-auto max-w-sm grid gap-1 mb-3 font-nunito text-sm" data-testid="sentence-checklist">
              {CHECKLIST.map(([key, label]) => {
                const v = result.checklist[key];
                return (
                  <li key={key} className="flex items-center gap-2" data-testid={`sentence-check-${key}`} data-ok={String(v)}>
                    <span aria-hidden="true">{v === true ? "✅" : v === false ? "✏️" : "⏳"}</span>
                    <span>{label}</span>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-2 bg-gradient-to-br from-[#f7f4fd] to-[#fff5f9] border border-[#d9ccf5] rounded-2xl p-4 flex items-start gap-3" data-testid="sentence-coach-bubble">
            <img src="/images/lumi/3d/Lumi_3D_Speaking.png" alt="" className="w-12 h-14 object-contain object-bottom shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-nunito font-bold text-purple-700 text-sm mb-0.5">Lumi says:</p>
              <p className="font-nunito text-foreground leading-relaxed" data-testid="sentence-lumi-feedback">{result.message}</p>
              {needsFix && result.sentence && (
                <p className="font-nunito text-sm text-muted-foreground mt-2" data-testid="sentence-your-sentence">Your sentence: {result.sentence}</p>
              )}
              {isCelebrating && !expanding && expansion?.question && (
                <p className="font-nunito text-foreground mt-3 font-bold" data-testid="sentence-expansion-question">🤔 {expansion.question}</p>
              )}
            </div>
          </div>

          {needsFix && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={tryAgain}
                className="bg-[#2fa6e0] hover:bg-[#2590c6] text-white font-nunito font-extrabold border-0 rounded-full px-6 py-5"
                data-testid="sentence-keep-going-btn"
              >
                Keep Going! ✏️
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SentenceBuilder;
